
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Building, Inbox, Settings, KeyRound, Image, FileText, Users, MessageSquare } from "lucide-react";
import { useCompanyStore, useCompanyRequestStore } from "@/lib/store";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import AdminHeader from "@/components/admin/AdminHeader";
import Footer from "@/components/Footer";
import CompaniesTable from "@/components/admin/CompaniesTable";
import CompanyRequestTables from "@/components/admin/CompanyRequestTables";
import SiteSettingsForm from "@/components/admin/SiteSettingsForm";
import { PasswordChangeForm } from "@/components/auth/PasswordChangeForm";
import ClientLogosManager from "@/components/admin/ClientLogosManager";
import ReviewsManagement from "@/components/admin/ReviewsManagement";
import CompanyClaimRequests from "@/components/admin/CompanyClaimRequests";
import ConnectionRequestsManagement from "@/components/admin/ConnectionRequestsManagement";
import { supabase } from "@/integrations/supabase/client";

const AdminDashboard = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("companies");
  const [isLoading, setIsLoading] = useState(true);
  const [pendingClaimCount, setPendingClaimCount] = useState(0);
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  
  const companies = useCompanyStore((state) => state.companies);
  const fetchCompanies = useCompanyStore((state) => state.fetchCompanies);
  const fetchSiteSettings = useCompanyStore((state) => state.fetchSiteSettings);
  
  const { requests } = useCompanyRequestStore();
  const fetchRequests = useCompanyRequestStore((state) => state.fetchRequests);

  const pendingRequests = requests.filter(req => req.status === 'pending');
  const processedRequests = requests.filter(req => req.status !== 'pending');

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchCompanies(),
          fetchRequests(),
          fetchSiteSettings()
        ]);
        
        // Fetch claim request count (using secure admin queries)
        const { count, error } = await supabase
          .from('company_claim_requests' as any)
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');
        
        if (error) {
          // Log error but don't expose details to console in production
          console.error("Error fetching claim requests count");
        } else if (count !== null) {
          setPendingClaimCount(count);
        }
        
        // Fetch connection request count
        const { count: connectionCount, error: connectionError } = await supabase
          .from('review_connections')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');
        
        if (connectionError) {
          console.error("Error fetching connection requests count");
        } else if (connectionCount !== null) {
          setPendingConnectionCount(connectionCount);
        }
        
      } catch (error) {
        console.error("Error loading dashboard data");
        toast({
          title: "Error loading data",
          description: "There was a problem loading your data. Please try refreshing.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [fetchCompanies, fetchRequests, fetchSiteSettings, toast]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <AdminHeader userEmail={user?.email} />
          
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-redflags-gray">Loading dashboard data...</p>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid grid-cols-7 w-full max-w-5xl">
                <TabsTrigger value="companies" className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Companies
                </TabsTrigger>
                <TabsTrigger value="requests" className="flex items-center gap-2">
                  <Inbox className="w-4 h-4" />
                  Requests
                  {pendingRequests.length > 0 && (
                    <Badge className="ml-2 bg-redflags-red text-white">
                      {pendingRequests.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="claims" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Claims
                  {pendingClaimCount > 0 && (
                    <Badge className="ml-2 bg-redflags-red text-white">
                      {pendingClaimCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="connections" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Connections
                  {pendingConnectionCount > 0 && (
                    <Badge className="ml-2 bg-redflags-red text-white">
                      {pendingConnectionCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="reviews" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Reviews
                </TabsTrigger>
                <TabsTrigger value="client-logos" className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Client Logos
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Settings
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="companies" className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <CompaniesTable companies={companies} />
                </div>
              </TabsContent>
              
              <TabsContent value="requests" className="space-y-4">
                <CompanyRequestTables 
                  pendingRequests={pendingRequests} 
                  processedRequests={processedRequests} 
                />
              </TabsContent>
              
              <TabsContent value="claims" className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-medium mb-6">Company Claim Requests</h2>
                  <CompanyClaimRequests />
                </div>
              </TabsContent>
              
              <TabsContent value="connections" className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <ConnectionRequestsManagement />
                </div>
              </TabsContent>
              
              <TabsContent value="reviews" className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <ReviewsManagement />
                </div>
              </TabsContent>
              
              <TabsContent value="client-logos" className="space-y-4">
                <ClientLogosManager />
              </TabsContent>
              
              <TabsContent value="settings" className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-medium mb-6">Site Settings</h2>
                  <SiteSettingsForm />
                </div>
                
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <KeyRound className="w-5 h-5 text-redflags-red" />
                    <h2 className="text-xl font-medium">Change Password</h2>
                  </div>
                  <PasswordChangeForm />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
      
      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};

export default AdminDashboard;
